import {
  Avatar,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InputMask from "react-input-mask";
import { UserRole, UserStatus } from "../../../types/types";
import { useEffect, useRef, useState } from "react";
import { PrimaryButton } from "../../../components/general/buttons/PrimaryButton";
import { userApi } from "../../../services";
import EditIcon from "@mui/icons-material/Edit";
import { useAuth } from "../../../context/AuthProvider/useAuth";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import AvatarImageIconButton from "../../../components/general/buttons/AvatarImageIconButton";

import BackPageButton, {
  backButtonContainer,
} from "../../../components/general/buttons/BackPageButton";
import {
  AddressProps,
  getCoordinatesFromAddress,
} from "../../../hooks/getCoodinatesByAddress";
import EmailIcon from "@mui/icons-material/Email";
import BadgeIcon from "@mui/icons-material/Badge";
import ClientNavbar from "../../../components/general/Navbars/ClientNavbar";
import { useNavigate } from "react-router-dom";
import { handleLogout } from "../../../utils/miscelaneous";
import LogoutIcon from "@mui/icons-material/Logout";
import { sharedStyles } from "@/styles/shared";
import useCep from "../../../hooks/useCep";
import { useTranslation } from "react-i18next";

//TODO: refactor entire CEP flow
const AccountDetails = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const navigate = useNavigate();

  const [profileImage, setProfileImage] = useState<HTMLCanvasElement[]>([]);
  const [userUpdateLoading, setUserUpdateLoading] = useState(false);

  const nameRef = useRef<HTMLInputElement>(null);
  const [role, setRole] = useState<UserRole>(auth.user.role);
  const phoneRef = useRef<HTMLInputElement>(null);

  const [cep, setCep] = useState<string>(auth.user.CEP);

  const streetRef = useRef<HTMLInputElement>(null);
  const numberRef = useRef<HTMLInputElement>(null);
  const districtRef = useRef<HTMLInputElement>(null);
  const cityRef = useRef<HTMLInputElement>(null);
  const stateRef = useRef<HTMLInputElement>(null);

  const [firstTimeFetched, setFirstTimeFetched] = useState(false);

  useEffect(() => {
    if (profileImage.length > 1) {
      setProfileImage([profileImage[profileImage.length - 1]]);
    }
  }, [profileImage]);

  const getProfileImageBlob = (): Promise<Blob | null> => {
    return new Promise((resolve) => {
      if (profileImage.length === 0) return resolve(null);

      const canvas = profileImage[0];
      canvas.toBlob((blob) => {
        resolve(blob);
      }, "image/jpeg", 0.9);
    });
  };

  const handleUpdateUser = async () => {
    setUserUpdateLoading(true);
    const hasChangedRole = auth.user.role !== role;

    try {
      const coordinates = await getCoordinatesFromAddress({
        street: streetRef.current?.value ?? "",
        number: numberRef.current?.value ?? "",
        district: districtRef.current?.value ?? "",
        city: cityRef.current?.value ?? "",
        state: stateRef.current?.value ?? "",
        country: "Brasil",
      } as AddressProps);

      const userDataDto = {
        name: nameRef.current?.value ?? "",
        role: role,
        phone: phoneRef.current?.value ?? "",
        CEP: cep,
        logradouro: streetRef.current?.value ?? "",
        number: numberRef.current?.value ?? "",
        district: districtRef.current?.value ?? "",
        city: cityRef.current?.value ?? "",
        state: stateRef.current?.value ?? "",
        country: "Brasil",
        lat: Number(coordinates[0]) ?? Number(0),
        lng: Number(coordinates[1]) ?? Number(0),
        status: UserStatus.OK,
      };

      const formData = new FormData();

      Object.entries(userDataDto).forEach(([key, value]) => {
        formData.append(key, String(value));
      });

      const imageBlob = await getProfileImageBlob();
      if (imageBlob) {
        formData.append("image", imageBlob, "profile_avatar.jpg");
      }
      const updatedUserResponse = await userApi.updateUser(
        auth.user.id,
          formData
      );

      if (updatedUserResponse) {
        auth.updateCurrentUser(updatedUserResponse);
        alert(t("accountDetails.alerts.updateSuccess"));
      }

      if (hasChangedRole) {
        navigate(role === UserRole.CLIENT ? "/home" : "/appointments");
      }
    } catch (error) {
      console.warn("Error updating user: ", error);
    } finally {
    setUserUpdateLoading(false);
  }
  };

  const fetchedCep = useCep(
    cep ?? "",
    streetRef,
    districtRef,
    cityRef,
    stateRef,
    firstTimeFetched
  );

  useEffect(() => {
    if (auth.user.CEP !== cep && !firstTimeFetched) setFirstTimeFetched(true);
  }, [cep, auth.user.CEP, firstTimeFetched]);

  const isUserGuest = auth.user.role === UserRole.GUEST;

  const renderSidebar = (
    <Box
      sx={{
        ...sharedStyles.verticalContent,
        alignItems: "center",
        width: "100%",
        paddingX: 3,
      }}
    >
      <Box>
        <Avatar
          src={
            profileImage.length === 0
              ? auth.user.image?.imageUrl
              : profileImage[0].toDataURL()
          }
          style={{ width: 100, height: 100 }}
        />

        {!isUserGuest && (
          <Tooltip title={t("accountDetails.sidebar.changeProfilePhoto")}>
            <Box>
              <AvatarImageIconButton
                icon={<CameraAltIcon />}
                setImages={setProfileImage}
              />
            </Box>
          </Tooltip>
        )}
      </Box>

      <Typography fontSize="14pt" fontWeight="bold" textAlign="center">
        {auth.user.name}
      </Typography>

      <Box sx={sharedStyles.verticalContent}>
        {auth.user.docNumber && (
          <Box sx={{ ...sharedStyles.horizontalContent }}>
            <BadgeIcon color="primary" />
            <Typography variant="body1" color="text.primary">
              {auth.user.docNumber}
            </Typography>
          </Box>
        )}

        <Box sx={{ ...sharedStyles.horizontalContent }}>
          <EmailIcon color="primary" />
          <Typography variant="body1" textAlign="justify" color="text.primary">
            {auth.user.email}
          </Typography>
        </Box>

        <Button
          onClick={() =>
            confirm(t("accountDetails.alerts.logoutConfirm")) &&
            handleLogout(auth, navigate)
          }
          disableRipple
          sx={{ textTransform: "none" }}
          startIcon={<LogoutIcon />}
          color="error"
        >
          {t("accountDetails.sidebar.logout")}
        </Button>
      </Box>
    </Box>
  );

  const renderPersonalData = (
    <Box
      sx={{
        ...sharedStyles.verticalContent,
        gap: 3,
        paddingX: { xs: 0, md: 0, lg: 15 },
      }}
    >
      <Typography variant="h6">{t("accountDetails.personalData.title")}</Typography>

      <Box sx={sharedStyles.verticalContent}>
        <TextField
          label={t("accountDetails.personalData.fullName")}
          size="small"
          fullWidth
          inputRef={nameRef}
          defaultValue={auth.user.name}
        />

        <FormControl fullWidth>
          <InputLabel>{t("accountDetails.personalData.accountType")}</InputLabel>
          <Select
            label={t("accountDetails.personalData.accountType")}
            value={role}
            onChange={(e: any) => setRole(e.target.value)}
          >
          <MenuItem value={UserRole.CLIENT}>
            {t("accountDetails.personalData.roles.client")}
          </MenuItem>
          <MenuItem value={UserRole.COMERCIAL}>
            {t("accountDetails.personalData.roles.commercial")}
          </MenuItem>
          </Select>
        </FormControl>

        <InputMask mask="(99) 9.9999-9999" maskChar="_">
          <TextField
            label={t("accountDetails.personalData.phone")}
            size="small"
            fullWidth
            inputRef={phoneRef}
            autoComplete="nope"
            defaultValue={auth.user.phone}
          />
        </InputMask>
      </Box>

      <Typography variant="h6">{t("accountDetails.address.title")}</Typography>

      <Box sx={sharedStyles.verticalContent}>
        <InputMask
          mask="99999-999"
          disabled={false}
          maskChar="_"
          value={cep}
          onChange={(e) => setCep(e.target.value)}
        >
        <TextField
            size="small"
            label={t("accountDetails.address.zipCode")}
            fullWidth
            autoComplete="nope"
        />
        </InputMask>

        <Box sx={sharedStyles.horizontalContent}>
          <TextField
            size="small"
            label={t("accountDetails.address.street")}
            sx={{ width: "75%" }}
            InputLabelProps={{ shrink: true }}
            inputRef={streetRef}
            disabled={
              fetchedCep?.street !== null && streetRef?.current?.value !== ""
            }
            defaultValue={auth.user.logradouro}
          />
          <TextField
            size="small"
            label={t("accountDetails.address.number")}
            sx={{ width: "25%" }}
            InputLabelProps={{ shrink: true }}
            inputRef={numberRef}
            defaultValue={auth.user.number}
          />
        </Box>

        <TextField
          size="small"
          label={t("accountDetails.address.district")}
          fullWidth
          InputLabelProps={{ shrink: true }}
          inputRef={districtRef}
          disabled={
            fetchedCep?.neighborhood !== null &&
            districtRef?.current?.value !== ""
          }
          defaultValue={auth.user.district}
        />

        <Box sx={sharedStyles.verticalContent}>
          <TextField
            size="small"
            label={t("accountDetails.address.city")}
            fullWidth
            InputLabelProps={{ shrink: true }}
            inputRef={cityRef}
            disabled={
              fetchedCep?.city !== null && cityRef?.current?.value !== ""
            }
            defaultValue={auth.user.city}
          />
          <TextField
            size="small"
            label={t("accountDetails.address.state")}
            fullWidth
            InputLabelProps={{ shrink: true }}
            inputRef={stateRef}
            disabled={
              fetchedCep?.state !== null && stateRef?.current?.value !== ""
            }
            defaultValue={auth.user.state}
          />
        </Box>

        <PrimaryButton
          label={t("accountDetails.actions.confirmEdit")}
          onClickAction={handleUpdateUser}
          isLoading={userUpdateLoading}
          icon={<EditIcon />}
          styles={{ height: "50px", color: "secondary.main", marginTop: 2 }}
        />
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        overflowX: "hidden",
        height: "100vh",
      }}
    >
      <ClientNavbar hideMidButton hideMenuButton />

      <Box sx={backButtonContainer}>
        <BackPageButton />
      </Box>

      <Box
        sx={{
          ...sharedStyles.horizontalContent,
          width: "100%",
          paddingX: 3,
          marginBottom: 5,
          justifyContent: "center",
          gap: { xs: 5, md: 3, lg: 1 },
          flexDirection: { xs: "column", md: "row", lg: "row" },
        }}
      >
        <Box
          sx={{
            width: { xs: "100%", md: "30%", lg: "30%" },
            alignSelf: "self-start",
          }}
        >
          {renderSidebar}
        </Box>

        {!isUserGuest && (
          <Box sx={{ width: { xs: "100%", md: "70%", lg: "70%" } }}>
            {renderPersonalData}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AccountDetails;
